import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  MdPerson,
  MdEmail,
  MdPhone,
  MdBusiness,
  MdBadge,
  MdArrowBack,
  MdCheckCircle,
  MdCancel,
} from 'react-icons/md';
import './UserProfile.css';

const API_URL =
  process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : 'http://localhost:5000';

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get(`/users/${id}/profile`);
        if (!cancelled) {
          setProfile(data.data);
          setAvatarError(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (id) fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const getInitials = (name) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const bgColor = profile?.name ? COLORS[profile.name.charCodeAt(0) % COLORS.length] : COLORS[0];
  const avatarSrc = profile?.avatar
    ? profile.avatar.startsWith('http')
      ? profile.avatar
      : `${API_URL}${profile.avatar}`
    : null;

  if (loading) {
    return (
      <main className="page">
        <div className="user-profile-page">
          <div className="user-profile-loading">
            <div className="spinner" />
            <span className="loading-text">Loading profile...</span>
          </div>
        </div>
      </main>
    );
  }

  if (error || !profile) {
    return (
      <main className="page">
        <div className="user-profile-page">
          <button
            type="button"
            className="user-profile-back"
            onClick={() => navigate(-1)}
          >
            <MdArrowBack size={18} /> Back
          </button>
          <div className="user-profile-error" style={{ marginTop: 24 }}>
            <p>{error || 'User not found'}</p>
          </div>
        </div>
      </main>
    );
  }

  const roleKey = (profile.role || 'staff').replace(/\s/g, '_');
  const roleLabel = (profile.role || 'staff').replace(/_/g, ' ');
  const managers = [
    ...(profile.managedBy ? [profile.managedBy] : []),
    ...(profile.managers && Array.isArray(profile.managers) ? profile.managers : []),
  ];
  const managerNames = managers
    .filter((m) => m && (m.name || m.email))
    .map((m) => m.name || m.email)
    .filter(Boolean);
  const isActive = profile.isActive !== false;

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="user-profile-info-row">
      <div className="user-profile-info-icon">
        <Icon size={22} />
      </div>
      <div className="user-profile-info-body">
        <p className="user-profile-info-label">{label}</p>
        <p className="user-profile-info-value">{value}</p>
      </div>
    </div>
  );

  return (
    <main className="page">
      <div className="user-profile-page">
        <div className="user-profile-cover-wrap">
          <div className="user-profile-back-wrap">
            <button
              type="button"
              className="user-profile-back"
              onClick={() => navigate(-1)}
            >
              <MdArrowBack size={20} /> Back
            </button>
          </div>
          <div className="user-profile-cover" />
        </div>

        <div className="user-profile-body">
          {/* Left column: profile summary only (small card) */}
          <aside className="user-profile-sidebar">
            <div className="user-profile-sidebar-card">
              <div
                className="user-profile-avatar-wrap"
                style={{ background: !avatarSrc || avatarError ? bgColor : 'transparent' }}
              >
                {avatarSrc && !avatarError ? (
                  <img
                    src={avatarSrc}
                    alt={profile.name}
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    {getInitials(profile.name)}
                  </span>
                )}
              </div>
              <h1 className="user-profile-name">{profile.name}</h1>
              <div className="user-profile-badges">
                <span className={`user-profile-role ${roleKey}`}>{roleLabel}</span>
                {profile.organization && (
                  <span className="user-profile-org">{profile.organization.name}</span>
                )}
              </div>
            </div>
          </aside>

          {/* Right column: contact & details — no card, full-width list */}
          <div className="user-profile-main">
            <h2 className="user-profile-section-title">Contact &amp; details</h2>
            <div className="user-profile-details-list">
              <InfoRow icon={MdEmail} label="Email" value={profile.email} />
              {profile.phone && (
                <InfoRow icon={MdPhone} label="Phone" value={profile.phone} />
              )}
              {profile.designation && (
                <InfoRow icon={MdBadge} label="Designation" value={profile.designation} />
              )}
              {profile.organization && (
                <InfoRow icon={MdBusiness} label="Organization" value={profile.organization.name} />
              )}
              <InfoRow
                icon={isActive ? MdCheckCircle : MdCancel}
                label="Status"
                value={
                  <span className={`user-profile-status ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                }
              />
              {managerNames.length > 0 && (
                <InfoRow
                  icon={MdPerson}
                  label="Managed by"
                  value={managerNames.join(', ')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default UserProfile;
